import { useEffect, useState } from "react";
import PageShell from "@/components/PageShell";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Link } from "react-router-dom";
import { Sparkle, TrendUp, Warning, Briefcase, Users, ClockCounterClockwise, CurrencyDollar, Target, ArrowUpRight } from "@phosphor-icons/react";
import TodayPanel from "@/components/TodayPanel";
import MethodDashboardStrip from "@/components/MethodDashboardStrip";
import HealthSparkline from "@/components/HealthSparkline";
import { CLIENT_TIER_LABEL, ENGAGEMENT_TYPE_LABEL, ENGAGEMENT_STAGE_LABEL, ENGAGEMENT_HEALTH_LABEL } from "@/lib/enumLabels";

function Stat({ label, value, sub, tone, icon: Icon, testid, to }) {
  // `tone` conveys meaning, never decoration.
  //   danger  → semantic destructive
  //   primary → text-action (lifted Navy accent in dark, Navy Ink in light)
  //             so figures like "Weighted pipeline" stay clearly readable in
  //             both themes without leaning on Brass (a governed accent).
  //   default → foreground
  const toneCls = tone === "danger" ? "text-destructive" : tone === "primary" ? "text-action" : "text-foreground";
  // Restrained hover / focus — a soft ring lift + border swap. Kept quiet so
  // the KPI row still reads as data, not as a bank of buttons. Full card is
  // the link target for a bigger, more accessible hit area.
  const interactiveCls = to
    ? "hover:border-ring/70 focus-visible:outline-none focus-visible:border-ring focus-visible:ring-2 focus-visible:ring-ring/30 transition-colors group cursor-pointer"
    : "";
  const inner = (
    <>
      <div className="flex items-start justify-between gap-2">
        <div className="text-[0.625rem] font-mono uppercase tracking-widest text-muted-foreground">{label}</div>
        {Icon && (
          <Icon
            size={14}
            className={`text-muted-foreground shrink-0 ${to ? "group-hover:text-foreground transition-colors" : ""}`}
          />
        )}
      </div>
      <div className={`mt-3 font-display text-2xl xl:text-3xl font-bold tracking-tight ${toneCls} tabular-nums`}>{value}</div>
      {sub && <div className="mt-1 text-xs text-muted-foreground">{sub}</div>}
    </>
  );
  if (to) {
    return (
      <Link to={to} data-testid={testid} aria-label={`${label} — open ${label.toLowerCase()} workspace`}
            className={`block border border-border bg-card p-4 xl:p-5 relative overflow-hidden ${interactiveCls}`}>
        {inner}
      </Link>
    );
  }
  return (
    <div className="border border-border bg-card p-4 xl:p-5 relative overflow-hidden" data-testid={testid}>
      {inner}
    </div>
  );
}

const fmt$ = (n) => n >= 1000000 ? `$${(n/1000000).toFixed(1)}M` : n >= 1000 ? `$${(n/1000).toFixed(0)}K` : `$${Math.round(n||0)}`;

export default function Dashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [clients, setClients] = useState([]);
  const [engagements, setEngagements] = useState([]);
  const [opps, setOpps] = useState([]);

  useEffect(() => {
    Promise.all([
      api.get("/dashboard/stats"),
      api.get("/clients"),
      api.get("/engagements"),
      api.get("/opportunities"),
    ]).then(([s, c, e, o]) => {
      setStats(s.data); setClients(c.data); setEngagements(e.data); setOpps(o.data);
    });
  }, []);

  const atRiskClients = clients.filter((c) => c.relationship_health === "at_risk" || c.relationship_health === "critical").slice(0, 5);
  const activeEngs = engagements.filter((e) => e.status === "active").slice(0, 6);
  const topOpps = [...opps].filter((o) => !["won", "lost"].includes(o.stage))
    .sort((a, b) => (b.value || 0) * b.probability - (a.value || 0) * a.probability).slice(0, 5);

  return (
    <PageShell size="default">
      {/* Morning Brief owns the top of the dashboard — no page-level
          eyebrow or heading; the panel's own masthead is the anchor. */}
      <div className="mb-8">
        <TodayPanel />
      </div>

      {/*
        Firm-wide summary metrics — five cards in a single responsive row.
        Breakpoints: 2-up on phones, 3-up on tablets, 5-up on standard
        desktops (≥1280px xl). Padding tightens one step at ≤xl so five
        cards still breathe at 1280 without becoming cramped. Each card is
        a Link — full-card hit target with a restrained focus ring.
      */}
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-3 xl:gap-4 mb-10">
        <Stat testid="stat-clients"     to="/clients"     label="Partners"          value={stats?.clients_count ?? "—"}                                       sub="Active book of business"                                             icon={Users} />
        <Stat testid="stat-engagements" to="/engagements" label="Active engagements" value={stats?.active_engagements ?? "—"}                                   sub={`${stats?.at_risk_engagements ?? 0} at risk`}                        icon={Briefcase} tone={stats?.at_risk_engagements > 0 ? "danger" : ""} />
        <Stat testid="stat-pipeline"    to="/pipeline"    label="Weighted pipeline"  value={fmt$(stats?.pipeline_weighted_value ?? 0)}                          sub={`${stats?.open_opportunities ?? 0} open opportunities`}              icon={Target} tone="primary" />
        <Stat testid="stat-revenue"     to="/invoices"    label="Revenue billed"     value={fmt$(stats?.revenue_billed ?? 0)}                                   sub="Across all engagements"                                              icon={CurrencyDollar} />
        <Stat testid="stat-tasks"       to="/tasks"       label="Open tasks"         value={stats?.open_tasks ?? "—"}                                           sub={`${stats?.overdue_tasks ?? 0} overdue`}                              icon={ClockCounterClockwise} tone={stats?.overdue_tasks > 0 ? "danger" : ""} />
      </div>

      {/* At-risk clients + Active engagements */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="border border-border bg-card lg:col-span-2">
          <div className="flex items-center justify-between border-b border-border px-5 py-3">
            <h2 className="font-display font-bold text-sm tracking-tight flex items-center gap-2"><Briefcase size={14} /> Active engagements</h2>
            <Link to="/engagements" className="text-[0.6875rem] font-mono uppercase tracking-widest text-eyebrow hover:underline flex items-center gap-1">View all <ArrowUpRight size={12} /></Link>
          </div>
          <table className="w-full text-sm">
            <thead className="text-[0.625rem] font-mono uppercase tracking-widest text-muted-foreground">
              <tr className="border-b border-border">
                <th className="text-left px-5 py-2 font-normal">Engagement</th>
                <th className="text-left px-3 py-2 font-normal">Stage</th>
                <th className="text-left px-3 py-2 font-normal">Health</th>
                <th className="text-right px-5 py-2 font-normal">Budget</th>
              </tr>
            </thead>
            <tbody>
              {activeEngs.length === 0 && <tr><td colSpan="4" className="px-5 py-8 text-center text-muted-foreground text-xs">No active engagements yet.</td></tr>}
              {activeEngs.map((e) => (
                <tr key={e.id} className="border-b border-border/50 hover:bg-secondary/30">
                  <td className="px-5 py-3">
                    <Link to={`/engagements/${e.id}`} className="hover:text-primary" data-testid={`dash-engagement-${e.id}`}>
                      <div className="font-medium">{e.name}</div>
                      <div className="text-[0.625rem] font-mono uppercase text-muted-foreground tracking-widest">{ENGAGEMENT_TYPE_LABEL[e.engagement_type] || e.engagement_type}</div>
                    </Link>
                  </td>
                  <td className="px-3 py-3 text-xs uppercase font-mono tracking-widest">{ENGAGEMENT_STAGE_LABEL[e.stage] || e.stage}</td>
                  <td className="px-3 py-3"><span className={`dot dot-${e.health}`} /> <span className="text-xs uppercase tracking-widest font-mono">{ENGAGEMENT_HEALTH_LABEL[e.health] || e.health}</span></td>
                  <td className="px-5 py-3 text-right data-cell">{fmt$(e.budget || 0)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="border border-border bg-card">
          <div className="flex items-center justify-between border-b border-border px-5 py-3">
            <h2 className="font-display font-bold text-sm tracking-tight flex items-center gap-2"><Warning size={14} className="text-destructive" /> Clients at risk</h2>
            <Link to="/clients" className="text-[0.6875rem] font-mono uppercase tracking-widest text-eyebrow hover:underline">All →</Link>
          </div>
          <div className="divide-y divide-border">
            {atRiskClients.length === 0 && <div className="px-5 py-8 text-center text-muted-foreground text-xs">No clients at risk. Green across the board.</div>}
            {atRiskClients.map((c) => (
              <Link key={c.id} to={`/clients/${c.id}`} className="block px-5 py-3 hover:bg-secondary/30" data-testid={`dash-client-${c.id}`}>
                <div className="flex items-center justify-between gap-3">
                  <div className="font-medium text-sm truncate">{c.name}</div>
                  <div className="flex items-center gap-2 shrink-0">
                    <HealthSparkline history={c.health_history} />
                    <span className={`dot dot-${c.relationship_health === "critical" ? "red" : "yellow"}`} />
                  </div>
                </div>
                <div className="text-[0.625rem] font-mono uppercase tracking-widest text-muted-foreground">{c.industry} · {CLIENT_TIER_LABEL[c.tier] || c.tier}</div>
              </Link>
            ))}
          </div>
        </div>
      </div>

      {/* Top opportunities */}
      <div className="mt-6 border border-border bg-card">
        <div className="flex items-center justify-between border-b border-border px-5 py-3">
          <h2 className="font-display font-bold text-sm tracking-tight flex items-center gap-2"><Target size={14} /> Top weighted opportunities</h2>
          <Link to="/pipeline" className="text-[0.6875rem] font-mono uppercase tracking-widest text-eyebrow hover:underline">Pipeline board →</Link>
        </div>
        <table className="w-full text-sm">
          <thead className="text-[0.625rem] font-mono uppercase tracking-widest text-muted-foreground">
            <tr className="border-b border-border">
              <th className="text-left px-5 py-2 font-normal">Opportunity</th>
              <th className="text-left px-3 py-2 font-normal">Stage</th>
              <th className="text-right px-3 py-2 font-normal">Value</th>
              <th className="text-right px-3 py-2 font-normal">Prob.</th>
              <th className="text-right px-5 py-2 font-normal">Weighted</th>
            </tr>
          </thead>
          <tbody>
            {topOpps.length === 0 && <tr><td colSpan="5" className="px-5 py-8 text-center text-muted-foreground text-xs">No open opportunities.</td></tr>}
            {topOpps.map((o) => (
              <tr key={o.id} className="border-b border-border/50 hover:bg-secondary/30">
                <td className="px-5 py-3">
                  <div className="font-medium">{o.name}</div>
                  <div className="text-[0.625rem] font-mono uppercase text-muted-foreground tracking-widest">{o.client_name || "—"}</div>
                </td>
                <td className="px-3 py-3 text-xs uppercase font-mono tracking-widest">{o.stage}</td>
                <td className="px-3 py-3 text-right data-cell">{fmt$(o.value || 0)}</td>
                <td className="px-3 py-3 text-right data-cell">{o.probability}%</td>
                <td className="px-5 py-3 text-right data-cell text-action">{fmt$((o.value || 0) * (o.probability / 100))}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/*
        Firm — Across the Method sits at the bottom of the dashboard so the
        page reads as a partner would triage in the morning: immediate
        priorities → firm-wide status → engagement and client health →
        growth opportunities → strategic Method overview. Kept in the same
        max-width column so responsive behaviour is unchanged.
      */}
      <div className="mt-8">
        <MethodDashboardStrip />
      </div>
    </PageShell>
  );
}
